import React, { useState, useEffect } from "react";
import { snapshotService } from "../services/snapshotService";
import { showToast } from "../utils/toast";

const Snapshot = () => {
  const [lastQuotes, setLastQuotes] = useState([]);
  const [lastTrades, setLastTrades] = useState([]);
  const [loading, setLoading] = useState(false); // Changed: Start with false instead of true
  const [error, setError] = useState("");
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("symbol");
  const [sortOrder, setSortOrder] = useState("asc");
  const [totalResults, setTotalResults] = useState(0);
  const [activeTab, setActiveTab] = useState("quotes");

  // API Key state
  const [apiKey, setApiKey] = useState("");
  const [apiKeyValid, setApiKeyValid] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [initializing, setInitializing] = useState(true); // Added: Track initialization

  // Modal state for symbol details
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [symbolData, setSymbolData] = useState(null);
  const [symbolLoading, setSymbolLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Load API key from localStorage on component mount
  useEffect(() => {
    const initializeApiKey = async () => {
      const savedApiKey = localStorage.getItem("snapshot_api_key");
      if (savedApiKey) {
        setApiKey(savedApiKey);
        await validateApiKey(savedApiKey);
      }
      setInitializing(false); // Mark initialization as complete
    };

    initializeApiKey();
  }, []);

  // Validate API key by making a test request
  const validateApiKey = async (key) => {
    if (!key || key.trim() === "") {
      setApiKeyValid(false);
      setError("Please enter your API key");
      return false;
    }

    try {
      setLoading(true);
      setError("");

      // Test API key with a small request
      const response = await snapshotService.getSnapshotData(key);

      if (response.success) {
        setApiKeyValid(true);
        setUserInfo({ email: response.user });
        localStorage.setItem("snapshot_api_key", key);
        showToast.success("API key validated successfully!");
        return true;
      } else {
        setApiKeyValid(false);
        setError(response.error || "Invalid API key");
        localStorage.removeItem("snapshot_api_key");
        showToast.error("Invalid API key");
        return false;
      }
    } catch (error) {
      console.error("API key validation error:", error);
      setApiKeyValid(false);
      setError("Failed to validate API key");
      localStorage.removeItem("snapshot_api_key");
      showToast.error("Failed to validate API key");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Handle API key submission
  const handleApiKeySubmit = async (e) => {
    e.preventDefault();
    const isValid = await validateApiKey(apiKey);
    if (isValid) {
      fetchSnapshotData();
    }
  };

  // Clear API key
  const clearApiKey = () => {
    setApiKey("");
    setApiKeyValid(false);
    setUserInfo(null);
    setLastQuotes([]);
    setLastTrades([]);
    setError("");
    setLoading(false); // Added: Reset loading state
    localStorage.removeItem("snapshot_api_key");
    showToast.info("API key cleared");
  };

  // Fetch snapshot data with API key
  const fetchSnapshotData = async (showLoadingSpinner = true) => {
    if (!apiKeyValid || !apiKey) {
      setError("Please provide a valid API key first");
      return;
    }

    try {
      if (showLoadingSpinner) setLoading(true);

      const response = await snapshotService.getSnapshotData(apiKey);
      console.log("Snapshot API Response:", response);

      if (response.success) {
        setLastQuotes(response.lastQuotes || []);
        setLastTrades(response.lastTrades || []);
        setTotalResults(response.totalResults || 0);
        setLastUpdate(new Date(response.timestamp));
        setUserInfo({ email: response.user });
        setError("");
      } else {
        setError(response.error || "Failed to fetch snapshot data");

        // Handle authentication errors
        if (
          response.error?.includes("Authentication") ||
          response.error?.includes("API key")
        ) {
          setApiKeyValid(false);
          localStorage.removeItem("snapshot_api_key");
        }
      }
    } catch (error) {
      console.error("Error fetching snapshot data:", error);
      setError("Network error. Please try again.");
    } finally {
      if (showLoadingSpinner) setLoading(false);
    }
  };

  // Fetch fresh data manually
  const fetchFreshData = async () => {
    if (!apiKeyValid || !apiKey) {
      showToast.error("Please provide a valid API key first");
      return;
    }

    try {
      setLoading(true);
      // Pass the API key to the service function
      const response = await snapshotService.getSnapshotData(apiKey);

      if (response.success) {
        setLastQuotes(response.lastQuotes || []);
        setLastTrades(response.lastTrades || []);
        setTotalResults(response.totalResults || 0);
        setLastUpdate(new Date(response.timestamp));
        setError("");
        showToast.success("Fresh data fetched successfully!");
      } else {
        setError(response.error || "Failed to fetch fresh data");
        showToast.error("Failed to fetch fresh data");
      }
    } catch (error) {
      console.error("Error fetching fresh data:", error);
      setError("Network error. Please try again.");
      showToast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch data for a specific symbol
  const fetchSymbolData = async (symbol) => {
    if (!apiKeyValid || !apiKey) {
      showToast.error("Please provide a valid API key first");
      return;
    }

    try {
      setSymbolLoading(true);
      setSelectedSymbol(symbol);
      setShowModal(true);

      const response = await snapshotService.getSymbolData(symbol, apiKey);

      if (response.success) {
        setSymbolData(response);
        showToast.success(`Data for ${symbol} fetched successfully!`);
      } else {
        setSymbolData(null);
        showToast.error(`Failed to fetch data for ${symbol}`);
      }
    } catch (error) {
      console.error("Error fetching symbol data:", error);
      setSymbolData(null);
      showToast.error("Network error. Please try again.");
    } finally {
      setSymbolLoading(false);
    }
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedSymbol(null);
    setSymbolData(null);
  };

  // Auto-refresh every 10 seconds (reduced frequency for API usage)
  useEffect(() => {
    if (autoRefresh && apiKeyValid) {
      const interval = setInterval(() => {
        fetchSnapshotData(false);
      }, 10000); // 10 seconds
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, apiKeyValid]);

  // Get current data based on active tab
  const getCurrentData = () => {
    return activeTab === "quotes" ? lastQuotes : lastTrades;
  };

  // Filter and sort data
  const processedData = React.useMemo(() => {
    const currentData = getCurrentData();
    if (!Array.isArray(currentData)) return [];

    let filtered = currentData.filter((item) =>
      item.s?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort data
    filtered.sort((a, b) => {
      let aVal, bVal;

      if (sortBy === "symbol") {
        aVal = a.s || "";
        bVal = b.s || "";
      } else if (activeTab === "quotes") {
        // Quote-specific sorting
        if (sortBy === "bid") {
          aVal = parseFloat(a.b) || 0;
          bVal = parseFloat(b.b) || 0;
        } else if (sortBy === "ask") {
          aVal = parseFloat(a.a) || 0;
          bVal = parseFloat(b.a) || 0;
        } else if (sortBy === "time") {
          aVal = a.t || 0;
          bVal = b.t || 0;
        }
      } else {
        // Trade-specific sorting
        if (sortBy === "price") {
          aVal = parseFloat(a.p) || 0;
          bVal = parseFloat(b.p) || 0;
        } else if (sortBy === "size") {
          aVal = parseFloat(a.sz) || 0;
          bVal = parseFloat(b.sz) || 0;
        } else if (sortBy === "time") {
          aVal = a.t || 0;
          bVal = b.t || 0;
        }
      }

      if (typeof aVal === "string") {
        return sortOrder === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      } else {
        return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
      }
    });

    return filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastQuotes, lastTrades, searchTerm, sortBy, sortOrder, activeTab]);

  // Format price with proper decimals
  const formatPrice = (price) => {
    if (!price) return "N/A";
    return parseFloat(price).toFixed(5);
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp).toLocaleTimeString();
  };

  // Show loading during initialization
  if (initializing) {
    return (
      <div className="container py-4">
        <div className="card shadow">
          <div className="card-body text-center py-5">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <h5>Initializing...</h5>
            <p className="text-muted">
              Checking for saved API key...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show API key form if not authenticated
  if (!apiKeyValid) {
    return (
      <div className="container py-4">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="card shadow">
              <div className="card-header bg-primary text-white text-center">
                <h4 className="mb-0">
                  <i className="bi bi-key me-2"></i>
                  API Authentication Required
                </h4>
              </div>
              <div className="card-body">
                <p className="text-muted text-center mb-4">
                  Please enter your API key to access real-time forex snapshot
                  data.
                </p>

                <form onSubmit={handleApiKeySubmit}>
                  <div className="mb-3">
                    <label htmlFor="apiKey" className="form-label fw-bold">
                      API Key
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      id="apiKey"
                      placeholder="Enter your API key"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      required
                      disabled={loading} // Added: Disable input during loading
                    />
                    <div className="form-text">
                      Your API key is required to authenticate and track your
                      usage.
                    </div>
                  </div>

                  {error && (
                    <div className="alert alert-danger">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="btn btn-primary w-100"
                    disabled={loading || !apiKey.trim()}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        ></span>
                        Validating...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-shield-check me-2"></i>
                        Validate & Continue
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-4 p-3 bg-light rounded">
                  <h6 className="fw-bold mb-2">
                    <i className="bi bi-info-circle me-2"></i>
                    How to get your API Key:
                  </h6>
                  <ol className="mb-0 small">
                    <li>Go to your account dashboard</li>
                    <li>Navigate to API Settings</li>
                    <li>Copy your API key</li>
                    <li>Paste it in the field above</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading && lastQuotes.length === 0 && lastTrades.length === 0) {
    return (
      <div className="container py-4">
        <div className="card shadow">
          <div className="card-body text-center py-5">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <h5>Loading Snapshot Data...</h5>
            <p className="text-muted">
              Fetching real-time forex snapshot data with your API key...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error && lastQuotes.length === 0 && lastTrades.length === 0) {
    return (
      <div className="container py-4">
        <div className="card shadow">
          <div className="card-body text-center py-5">
            <div className="text-danger mb-3">
              <i
                className="bi bi-exclamation-triangle"
                style={{ fontSize: "3rem" }}
              ></i>
            </div>
            <h5 className="text-danger">Error Loading Data</h5>
            <p className="text-muted mb-4">{error}</p>
            <div className="d-flex gap-2 justify-content-center">
              <button
                className="btn btn-primary"
                onClick={() => fetchSnapshotData()}
              >
                <i className="bi bi-arrow-clockwise me-2"></i>
                Retry
              </button>
              <button
                className="btn btn-outline-secondary"
                onClick={clearApiKey}
              >
                <i className="bi bi-key me-2"></i>
                Change API Key
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="card shadow">
        <div className="card-header bg-primary text-white">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h3 className="mb-1">Real-Time Forex Snapshot</h3>
              <small>
                Live data updated every 3 seconds |
                {lastUpdate &&
                  ` Last update: ${lastUpdate.toLocaleTimeString()}`}
                {userInfo && ` | User: ${userInfo.email}`}
              </small>
            </div>
            <div className="d-flex align-items-center gap-3">
              {loading && (
                <div
                  className="spinner-border spinner-border-sm text-white"
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
              )}
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="autoRefreshSwitch"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                />
                <label
                  className="form-check-label text-white"
                  htmlFor="autoRefreshSwitch"
                >
                  Auto-refresh (10s)
                </label>
              </div>
              <button
                className="btn btn-outline-light btn-sm"
                onClick={clearApiKey}
                title="Change API Key"
              >
                <i className="bi bi-key"></i>
              </button>
            </div>
          </div>
        </div>

        <div className="card-body">
          {/* Tabs */}
          <div className="row mb-4">
            <div className="col-12">
              <ul className="nav nav-tabs">
                <li className="nav-item">
                  <button
                    className={`nav-link ${
                      activeTab === "quotes" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("quotes")}
                  >
                    <i className="bi bi-currency-exchange me-2"></i>
                    Last Quotes ({lastQuotes.length})
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${
                      activeTab === "trades" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("trades")}
                  >
                    <i className="bi bi-activity me-2"></i>
                    Last Trades ({lastTrades.length})
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Controls */}
          <div className="row mb-4">
            <div className="col-md-4">
              <label className="form-label fw-bold">Search Symbol:</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search forex pairs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-bold">Sort By:</label>
              <select
                className="form-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="symbol">Symbol</option>
                {activeTab === "quotes" ? (
                  <>
                    <option value="bid">Bid</option>
                    <option value="ask">Ask</option>
                  </>
                ) : (
                  <>
                    <option value="price">Price</option>
                    <option value="size">Size</option>
                  </>
                )}
                <option value="time">Time</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-bold">Order:</label>
              <select
                className="form-select"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label fw-bold">Actions:</label>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-success"
                  onClick={fetchFreshData}
                  disabled={loading}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Fresh Data
                </button>
                <button
                  className="btn btn-outline-primary"
                  onClick={() => fetchSnapshotData()}
                  disabled={loading}
                >
                  <i className="bi bi-arrow-repeat me-2"></i>
                  Refresh
                </button>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="alert alert-info mb-4">
            <div className="row text-center">
              <div className="col-md-3">
                <strong>Total Results:</strong> {totalResults}
              </div>
              <div className="col-md-3">
                <strong>Displayed Items:</strong> {processedData.length}
              </div>
              <div className="col-md-3">
                <strong>Update Frequency:</strong> Every 3 seconds
              </div>
              <div className="col-md-3">
                <strong>Status:</strong>
                <span className="badge bg-success ms-1">
                  <i className="bi bi-shield-check me-1"></i>
                  Authenticated
                </span>
              </div>
            </div>
          </div>

          {/* Data Tables */}
          {activeTab === "quotes" ? (
            // Quotes Table
            <div className="table-responsive">
              <table className="table table-striped table-hover">
                <thead className="table-dark">
                  <tr>
                    <th>Symbol</th>
                    <th>Bid</th>
                    <th>Ask</th>
                    <th>Bid Size</th>
                    <th>Ask Size</th>
                    <th>Time</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {processedData.map((item, index) => (
                    <tr key={item.s || index}>
                      <td>
                        <strong className="text-primary">
                          {item.s || "N/A"}
                        </strong>
                      </td>
                      <td className="text-danger fw-bold">
                        {formatPrice(item.b)}
                      </td>
                      <td className="text-success fw-bold">
                        {formatPrice(item.a)}
                      </td>
                      <td>{item.bsz || "N/A"}</td>
                      <td>{item.asz || "N/A"}</td>
                      <td>
                        <small className="text-muted">
                          {formatTime(item.t)}
                        </small>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => fetchSymbolData(item.s)}
                          disabled={symbolLoading}
                        >
                          <i className="bi bi-eye me-1"></i>
                          Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            // Trades Table
            <div className="table-responsive">
              <table className="table table-striped table-hover">
                <thead className="table-dark">
                  <tr>
                    <th>Symbol</th>
                    <th>Price</th>
                    <th>Size</th>
                    <th>Time</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {processedData.map((item, index) => (
                    <tr key={item.s || index}>
                      <td>
                        <strong className="text-primary">
                          {item.s || "N/A"}
                        </strong>
                      </td>
                      <td className="fw-bold">{formatPrice(item.p)}</td>
                      <td>{item.sz || "N/A"}</td>
                      <td>
                        <small className="text-muted">
                          {formatTime(item.t)}
                        </small>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => fetchSymbolData(item.s)}
                          disabled={symbolLoading}
                        >
                          <i className="bi bi-eye me-1"></i>
                          Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {processedData.length === 0 && (
            <div className="text-center py-5">
              <i
                className="bi bi-search"
                style={{ fontSize: "3rem", color: "#6c757d" }}
              ></i>
              <h5 className="mt-3 text-muted">No data found</h5>
              <p className="text-muted">
                {searchTerm
                  ? "Try adjusting your search term"
                  : `No ${activeTab} data available`}
              </p>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="alert alert-warning mt-3">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Symbol Detail Modal (keep existing modal code) */}
      {showModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  <i className="bi bi-graph-up me-2"></i>
                  {selectedSymbol} - Detailed Information
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={closeModal}
                ></button>
              </div>
              <div className="modal-body">
                {symbolLoading ? (
                  <div className="text-center py-4">
                    <div
                      className="spinner-border text-primary mb-3"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p>Loading detailed data for {selectedSymbol}...</p>
                  </div>
                ) : symbolData ? (
                  <div className="row">
                    <div className="col-md-6">
                      <div className="card h-100">
                        <div className="card-header bg-light">
                          <h6 className="mb-0">
                            <i className="bi bi-currency-exchange me-2"></i>
                            Quote Information
                          </h6>
                        </div>
                        <div className="card-body">
                          {symbolData.quote ? (
                            <>
                              <div className="row mb-3">
                                <div className="col-6">
                                  <strong>Symbol:</strong>
                                  <div className="text-primary fs-5">
                                    {symbolData.quote.s}
                                  </div>
                                </div>
                                <div className="col-6">
                                  <strong>Mid Price:</strong>
                                  <div className="text-success fs-5">
                                    {formatPrice(
                                      (symbolData.quote.a +
                                        symbolData.quote.b) /
                                        2
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="row mb-3">
                                <div className="col-6">
                                  <strong>Bid:</strong>
                                  <div className="text-danger fs-6">
                                    {formatPrice(symbolData.quote.b)}
                                  </div>
                                </div>
                                <div className="col-6">
                                  <strong>Ask:</strong>
                                  <div className="text-success fs-6">
                                    {formatPrice(symbolData.quote.a)}
                                  </div>
                                </div>
                              </div>
                              <div className="row mb-3">
                                <div className="col-6">
                                  <strong>Bid Size:</strong>
                                  <div>{symbolData.quote.bsz || "N/A"}</div>
                                </div>
                                <div className="col-6">
                                  <strong>Ask Size:</strong>
                                  <div>{symbolData.quote.asz || "N/A"}</div>
                                </div>
                              </div>
                              <div className="row">
                                <div className="col-12">
                                  <strong>Quote Time:</strong>
                                  <div className="text-muted small">
                                    {formatTime(symbolData.quote.t)}
                                  </div>
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="text-center py-3">
                              <i
                                className="bi bi-info-circle text-muted mb-2"
                                style={{ fontSize: "2rem" }}
                              ></i>
                              <p className="text-muted">
                                No quote data available
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card h-100">
                        <div className="card-header bg-light">
                          <h6 className="mb-0">
                            <i className="bi bi-activity me-2"></i>
                            Trade Information
                          </h6>
                        </div>
                        <div className="card-body">
                          {symbolData.trade ? (
                            <>
                              <div className="row mb-3">
                                <div className="col-12">
                                  <strong>Symbol:</strong>
                                  <div className="text-primary fs-5">
                                    {symbolData.trade.s}
                                  </div>
                                </div>
                              </div>
                              <div className="row mb-3">
                                <div className="col-6">
                                  <strong>Last Price:</strong>
                                  <div className="text-primary fs-5">
                                    {formatPrice(symbolData.trade.p)}
                                  </div>
                                </div>
                                <div className="col-6">
                                  <strong>Size:</strong>
                                  <div>{symbolData.trade.sz || "N/A"}</div>
                                </div>
                              </div>
                              <div className="row">
                                <div className="col-12">
                                  <strong>Trade Time:</strong>
                                  <div className="text-muted small">
                                    {formatTime(symbolData.trade.t)}
                                  </div>
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="text-center py-3">
                              <i
                                className="bi bi-info-circle text-muted mb-2"
                                style={{ fontSize: "2rem" }}
                              ></i>
                              <p className="text-muted">
                                No trade data available
                              </p>
                            </div>
                          )}
                          <div className="alert alert-info mt-3">
                            <i className="bi bi-info-circle me-2"></i>
                            <small>
                              This data is updated every 3 seconds from live
                              market feeds.
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <div className="text-warning mb-3">
                      <i
                        className="bi bi-exclamation-triangle"
                        style={{ fontSize: "2rem" }}
                      ></i>
                    </div>
                    <h6>No Data Available</h6>
                    <p className="text-muted">
                      Could not fetch detailed data for {selectedSymbol}
                    </p>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                >
                  Close
                </button>
                {selectedSymbol && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => fetchSymbolData(selectedSymbol)}
                    disabled={symbolLoading}
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Refresh Data
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Snapshot;
